import { isRouteActive } from '../utils/routeMatch';

// The menu tree comes from the API (Master.Menu) and decides WHO sees WHAT.
// This module decides how it is PRESENTED: logical groups, one icon family,
// at most two levels, and a single active page. Items it does not know keep
// the group, icon and order the API gave them.

// Order here is the order in the sidebar.
export const NAV_GROUPS = [
  { id: 'overview', label: 'Overview' },
  { id: 'procurement', label: 'Procurement' },
  { id: 'tender', label: 'Tender' },
  { id: 'evaluation', label: 'Evaluation & Award' },
  { id: 'master', label: 'Master Data' },
  { id: 'admin', label: 'Administration' },
];

// First path segment of the menu link -> group, icon. Position in each group
// follows the procurement lifecycle, not the API order.
const NAV_ITEMS = {
  overview: [
    ['dashboard', 'ri-dashboard-3-line'],
    ['dashboard-procurement', 'ri-dashboard-3-line'],
    ['apps-mailbox', 'ri-mail-line'],
  ],
  procurement: [
    ['apps-procurement', 'ri-stack-line'],
    ['apps-requesthps', 'ri-file-edit-line'],
    ['apps-hps', 'ri-price-tag-3-line'],
    ['apps-freightcost', 'ri-ship-line'],
  ],
  tender: [
    ['apps-invitation', 'ri-mail-send-line'],
    ['apps-offer', 'ri-hand-coin-line'],
    ['apps-tenderopening', 'ri-lock-unlock-line'],
    ['apps-improvementclarification', 'ri-question-answer-line'],
    ['apps-negotiation-supplier', 'ri-discuss-line'],
    ['apps-awarding-supplier', 'ri-award-line'],
  ],
  evaluation: [
    ['apps-evaluation-invitation', 'ri-mail-check-line'],
    ['apps-tenderevaluation', 'ri-survey-line'],
    ['apps-negotiation', 'ri-discuss-line'],
    ['apps-awarding', 'ri-award-line'],
  ],
  master: [
    ['apps-product', 'ri-box-3-line'],
    ['apps-product-type-view', 'ri-apps-2-line'],
    ['apps-indexprice', 'ri-line-chart-line'],
    ['apps-incoterm-view', 'ri-exchange-line'],
    ['apps-incoterm-location-view', 'ri-map-pin-line'],
    ['apps-location-view', 'ri-map-pin-2-line'],
    ['apps-jetty-location-view', 'ri-anchor-line'],
    ['apps-payment-currency', 'ri-money-dollar-circle-line'],
    ['apps-law-view', 'ri-government-line'],
    ['apps-arbitration-view', 'ri-hammer-line'],
    ['apps-clause', 'ri-article-line'],
    ['apps-document-template', 'ri-file-copy-2-line'],
    ['apps-document-upload-master', 'ri-upload-cloud-2-line'],
    ['apps-tender-announcement', 'ri-megaphone-line'],
    ['apps-gtc-view', 'ri-file-text-line'],
    ['apps-gtc-amend', 'ri-file-edit-line'],
  ],
  admin: [
    ['apps-usermanagement', 'ri-user-settings-line'],
    ['apps-roles', 'ri-team-line'],
    ['apps-role', 'ri-team-line'],
    ['apps-permission', 'ri-key-2-line'],
    ['apps-role-permission', 'ri-shield-keyhole-line'],
    ['apps-position-id', 'ri-profile-line'],
    ['apps-idaman-user', 'ri-group-line'],
    ['apps-menumanagement', 'ri-menu-line'],
    ['apps-menuassignment', 'ri-node-tree'],
    ['apps-api-key', 'ri-code-s-slash-line'],
    ['version', 'ri-information-line'],
  ],
};

// Pages reached from the profile dropdown instead of the sidebar. The API
// still grants them, so permission checks keep working.
const USER_MENU_SEGMENTS = new Set(['apps-activity-log']);

const KNOWN = {};
Object.entries(NAV_ITEMS).forEach(([group, entries]) =>
  entries.forEach(([segment, icon], index) => {
    KNOWN[segment] = { group, icon, index };
  })
);

// API header labels that mean one of our groups, so unknown items under
// them still land in the right place.
const HEADER_ALIASES = {
  menu: 'overview',
  overview: 'overview',
  procurement: 'procurement',
  'plan & procurement': 'procurement',
  tender: 'tender',
  'tender evaluation': 'evaluation',
  'evaluation & award': 'evaluation',
  'master data': 'master',
  'user management': 'admin',
  administration: 'admin',
};

// Legacy icon fonts still stored in Master.Menu -> Remix line (ICONS.md).
const LEGACY_ICONS = {
  'bx-bar-chart-alt-2': 'ri-dashboard-3-line',
  'bx-layer': 'ri-stack-line',
  'bx-detail': 'ri-file-edit-line',
  'bxs-file-doc': 'ri-price-tag-3-line',
  'bx-mail-send': 'ri-mail-send-line',
  'bxs-offer': 'ri-hand-coin-line',
  'bx-notepad': 'ri-survey-line',
  'bx-award': 'ri-award-line',
  'bx-user-circle': 'ri-user-settings-line',
  'bx-select-multiple': 'ri-team-line',
  'bx-key': 'ri-key-2-line',
  'bx-shield-alt-2': 'ri-shield-keyhole-line',
  'bx-package': 'ri-box-3-line',
  'bx-trending-up': 'ri-line-chart-line',
  'bx-transfer': 'ri-exchange-line',
  'bx-book-content': 'ri-article-line',
  'bx-shield-quarter': 'ri-hammer-line',
  'bx-map-pin': 'ri-map-pin-line',
  'bx-category-alt': 'ri-apps-2-line',
  'bx-money': 'ri-money-dollar-circle-line',
  'bx-cloud-upload': 'ri-upload-cloud-2-line',
  'bx-dollar-circle': 'ri-ship-line',
  'bx-envelope': 'ri-mail-check-line',
  'bx-history': 'ri-history-line',
};

export const FALLBACK_ICON = 'ri-file-list-3-line';

const segmentOf = link =>
  String(link || '')
    .split('/')
    .filter(Boolean)[0] || '';

const hasLink = link => !!link && link !== '/' && link !== '/#';

const isCrude = link => segmentOf(link) === 'crude';

// Crude keeps its own icons and grouping from the API.
const knownFor = link => (isCrude(link) ? null : KNOWN[segmentOf(link)]);

/** Always returns a single-family `ri-*` icon class. */
export const resolveNavIcon = (item, known) => {
  if (known) return known.icon;
  const raw = String(item.icon || '').trim();
  const ri = raw.split(/\s+/).find(c => /^ri-[a-z0-9-]+$/.test(c));
  if (ri) return ri.replace(/-fill$/, '-line');
  const legacy = raw.split(/\s+/).find(c => LEGACY_ICONS[c]);
  return legacy ? LEGACY_ICONS[legacy] : FALLBACK_ICON;
};

const byOrder = (a, b) => (a.orderNumber || 0) - (b.orderNumber || 0);

// Visible children of an API item, flattened to a single level.
const collectChildren = item => {
  const out = [];
  [...(item.children || [])].sort(byOrder).forEach(child => {
    if (child.isHeader || !child.canRead) return;
    if (hasLink(child.link) && !USER_MENU_SEGMENTS.has(segmentOf(child.link)))
      out.push(child);
    out.push(...collectChildren(child));
  });
  return out;
};

const toSub = child => ({
  id: child.code || child.id,
  label: child.label,
  link: child.link,
});

// API item -> presentable node, or null when it leads nowhere.
const toNode = (item, header) => {
  if (item.isHeader || !item.canRead) return null;

  const subs = collectChildren(item).map(toSub);

  // A parent without its own page that wraps a single page is just that page.
  if (!hasLink(item.link) && subs.length === 1) {
    const only = subs[0];
    return toNode(
      {
        ...item,
        code: only.id,
        label: only.label,
        link: only.link,
        children: [],
      },
      header
    );
  }
  if (!hasLink(item.link) && !subs.length) return null;

  const known = knownFor(item.link);
  return {
    id: item.code || item.id,
    label: item.label,
    link: hasLink(item.link) ? item.link : subs[0].link,
    ownLink: hasLink(item.link),
    icon: resolveNavIcon(item, known),
    known,
    header,
    orderNumber: item.orderNumber || 0,
    subs: hasLink(item.link) ? subs.filter(s => s.link !== item.link) : subs,
  };
};

const groupIdFor = node => {
  if (node.known) return node.known.group;
  const alias = HEADER_ALIASES[String(node.header?.label || '').toLowerCase()];
  if (alias) return alias;
  return node.header ? `api:${node.header.id}` : 'api:root';
};

/**
 * @param {Array} tree   menu tree from /menu/me
 * @param {string} path  current location.pathname
 * @returns {{groups, trail}} groups -> items -> subs, each flagged with
 *   isActive (this is the current page) / hasActive (page is inside it);
 *   trail is {group, module, page} for the current page, or null.
 */
export const buildNavigation = (tree, path) => {
  const nodes = [];
  const seenLinks = new Set();

  const push = (item, header) => {
    if (USER_MENU_SEGMENTS.has(segmentOf(item.link))) return;
    const node = toNode(item, header);
    if (!node || seenLinks.has(node.link)) return;
    seenLinks.add(node.link);
    nodes.push(node);
  };

  [...(tree || [])].sort(byOrder).forEach(top => {
    if (!top.isHeader) return push(top, null);
    const header = {
      id: top.code || top.id,
      label: top.label,
      orderNumber: top.orderNumber || 0,
    };
    [...(top.children || [])].sort(byOrder).forEach(c => push(c, header));
  });

  const groups = new Map();
  nodes.forEach(node => {
    const id = groupIdFor(node);
    if (!groups.has(id)) {
      const std = NAV_GROUPS.find(g => g.id === id);
      groups.set(id, {
        id,
        label: std ? std.label : node.header?.label || '',
        rank: std
          ? NAV_GROUPS.indexOf(std)
          : NAV_GROUPS.length + (node.header?.orderNumber || 0),
        items: [],
      });
    }
    groups.get(id).items.push(node);
  });

  const ordered = [...groups.values()].sort((a, b) => a.rank - b.rank);
  ordered.forEach(group =>
    group.items.sort((a, b) => {
      const ai = a.known ? a.known.index : 1000 + a.orderNumber;
      const bi = b.known ? b.known.index : 1000 + b.orderNumber;
      return ai - bi;
    })
  );

  // One active page: first sub-page match, else first module match.
  let trail = null;
  for (const group of ordered) {
    for (const node of group.items) {
      const sub = node.subs.find(s => isRouteActive(s.link, path));
      if (sub || (node.ownLink && isRouteActive(node.link, path))) {
        trail = { group, module: node, page: sub || null };
        break;
      }
    }
    if (trail) break;
  }

  ordered.forEach(group => {
    group.isActive = trail?.group === group;
    group.items.forEach(node => {
      node.hasActive = trail?.module === node;
      node.isActive = node.hasActive && !trail.page;
      node.subs.forEach(s => {
        s.isActive = trail?.page === s;
      });
    });
  });

  return { groups: ordered, trail };
};
